import { renderMarkdown } from '@/common/marked/markdown.utils.js';

/**
 * Print styles to inject into the iframe for proper PDF output.
 * These styles ensure the content prints correctly across pages.
 */
const PRINT_STYLES = `
    @page {
        size: letter;
        margin: 0.75in;
    }

    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    body {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 12pt;
        line-height: 1.6;
        color: #3A3C45;
        margin: 0;
        padding: 0;
    }

    h1, h2 {
        font-family: 'Sora', 'Inter', sans-serif;
        color: #2A2C35;
        page-break-after: avoid;
    }

    h3, h4, h5, h6 {
        font-family: 'Inter', sans-serif;
        color: #2A2C35;
        page-break-after: avoid;
    }

    h1 { font-size: 24pt; margin-top: 0; margin-bottom: 16pt; }
    h2 { font-size: 18pt; margin-top: 24pt; margin-bottom: 10pt; }
    h3 { font-size: 15pt; margin-top: 18pt; margin-bottom: 8pt; }
    h4 { font-size: 14pt; margin-top: 18pt; margin-bottom: 8pt; }
    h5 { font-size: 12pt; margin-top: 14pt; margin-bottom: 6pt; }
    h6 { font-size: 11pt; margin-top: 14pt; margin-bottom: 6pt; }

    p {
        margin-top: 0;
        margin-bottom: 12pt;
        orphans: 3;
        widows: 3;
    }

    a {
        color: #1B8296;
        text-decoration: none;
    }

    code {
        font-family: 'JetBrains Mono', 'Courier New', monospace;
        font-size: 10pt;
        background-color: #D7EFF6;
        color: #01697E;
        padding: 2pt 4pt;
        border-radius: 3pt;
    }

    pre {
        font-family: 'JetBrains Mono', 'Courier New', monospace;
        font-size: 10pt;
        background-color: #1C1D23;
        color: #DADCE5;
        padding: 12pt;
        border-radius: 6pt;
        overflow-x: auto;
        page-break-inside: avoid;
    }

    pre code {
        background-color: transparent;
        color: inherit;
        padding: 0;
    }

    blockquote {
        margin: 12pt 0;
        padding: 10pt 16pt;
        background-color: #FCFCFD;
        border-left: 4pt solid #01697E;
        font-style: italic;
        page-break-inside: avoid;
    }

    blockquote p {
        margin-bottom: 0;
    }

    ul, ol {
        margin-top: 0;
        margin-bottom: 12pt;
        padding-left: 24pt;
    }

    li {
        margin-bottom: 4pt;
    }

    hr {
        border: none;
        border-top: 1px solid #EAECF1;
        margin: 16pt 0;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        margin: 12pt 0;
        page-break-inside: avoid;
    }

    th, td {
        border: 1px solid #DADCE5;
        padding: 8pt 12pt;
        text-align: left;
    }

    th {
        background-color: #F8F8F9;
        font-weight: 600;
    }

    img {
        max-width: 100%;
        height: auto;
        page-break-inside: avoid;
    }

    /* Task list checkboxes */
    input[type="checkbox"] {
        margin-right: 8pt;
    }
`;

/**
 * Font face declarations to load fonts in the iframe.
 * Uses the same CDN fonts as the main application.
 */
const FONT_FACES = `
    @font-face {
        font-family: 'Inter';
        font-style: normal;
        font-weight: 400;
        src: url('https://cdn.pendo.io/fonts/inter-regular/inter-regular.woff2') format('woff2');
    }

    @font-face {
        font-family: 'Inter';
        font-style: normal;
        font-weight: 600;
        src: url('https://cdn.pendo.io/fonts/inter-semibold/inter-semibold.woff2') format('woff2');
    }

    @font-face {
        font-family: 'Inter';
        font-style: normal;
        font-weight: 700;
        src: url('https://cdn.pendo.io/fonts/inter-bold/inter-bold.woff2') format('woff2');
    }

    @font-face {
        font-family: 'Sora';
        font-style: normal;
        font-weight: 700;
        src: url('https://cdn.pendo.io/fonts/sora-bold/sora-bold.woff2') format('woff2');
    }
`;

/**
 * Creates an HTML document string for the iframe.
 *
 * @param {string} htmlContent - The rendered markdown HTML content
 * @param {string} title - Document title
 * @returns {string} Complete HTML document
 */
function createPrintDocument (htmlContent, title = 'Document') {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${title}</title>
    <style>
        ${FONT_FACES}
        ${PRINT_STYLES}
    </style>
</head>
<body>
    <div class="markdown-content">
        ${htmlContent}
    </div>
</body>
</html>
    `.trim();
}

/**
 * Prints markdown content using a hidden iframe and the browser's print dialog.
 * This opens the system print dialog, allowing the user to save as PDF or print.
 *
 * @param {string} markdownContent - The markdown content to print
 * @param {string} title - Document title (shown in print dialog)
 * @returns {Promise<void>} Resolves when print dialog is opened
 *
 * @example
 * await printMarkdown('# Hello World\n\nThis is a paragraph.', 'My Document');
 */
export async function printDocument (markdownContent, title = 'Document') {
    // Render markdown to HTML
    const htmlContent = renderMarkdown(markdownContent);

    // Create the full HTML document
    const printDocument = createPrintDocument(htmlContent, title);

    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '100%';
    iframe.style.bottom = '100%';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';

    // Return a promise that resolves when printing is initiated
    return new Promise((resolve, reject) => {
        iframe.onload = () => {
            try {
                // Write the document content to the iframe
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                iframeDoc.open();
                iframeDoc.write(printDocument);
                iframeDoc.close();

                // Wait for fonts to load before printing
                if (iframe.contentDocument.fonts && iframe.contentDocument.fonts.ready) {
                    iframe.contentDocument.fonts.ready.then(() => {
                        // Small delay to ensure rendering is complete
                        setTimeout(() => {
                            iframe.contentWindow.focus();
                            iframe.contentWindow.print();

                            // Clean up iframe after a delay
                            setTimeout(() => {
                                document.body.removeChild(iframe);
                                resolve();
                            }, 1000);
                        }, 100);
                    });
                } else {
                    // Fallback for browsers without font loading API
                    setTimeout(() => {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();

                        setTimeout(() => {
                            document.body.removeChild(iframe);
                            resolve();
                        }, 1000);
                    }, 500);
                }
            } catch (error) {
                document.body.removeChild(iframe);
                reject(error);
            }
        };

        iframe.onerror = (error) => {
            document.body.removeChild(iframe);
            reject(error);
        };

        // Append iframe to trigger load
        document.body.appendChild(iframe);
    });
}
